import type { RootState } from 'client/js/redux';
import { createSelector } from 'reselect';
import { ProductFlagService } from '../../../../shared/ProductFlag.service';
import type { ProductFlagValues } from '../../../../shared/ProductFlipperFlag.service';
import { DARKLY_PRODUCT_FLAGS } from '../../../../shared/constants/productFlags';
import type { DarklyProductFlag, LegacyProductSuite } from '../../../../shared/constants/productFlags';
import { LEGACY_PRODUCT_SUITE_FLAGS } from '../../../../shared/constants/productFlags';
import type { LegacyProductFlag } from '../../../../shared/constants/productFlipperFlagRelation';
import { getFlipperFlagValues } from '../../redux/domains/FlipperUpdates/Flipper.selectors';
import { VidaliaProductService } from '../services/VidaliaProductService';

function getProductName(_: RootState, productName: LegacyProductFlag): string {
  return productName;
}

function getProductSuite(_: RootState, productSuite: LegacyProductSuite): LegacyProductSuite {
  return productSuite;
}

const EMPTY_OBJECT = {};

export function getProductFlagValues(): ProductFlagValues {
  return (<any>window)?.App?.context?.ppp || EMPTY_OBJECT;
}

export const isProductEnabled: (state: RootState, productName: LegacyProductFlag | DarklyProductFlag) => boolean =
  createSelector(getFlipperFlagValues, getProductFlagValues, getProductName, (flipperFlags, productFlags, productName) => {
    return new ProductFlagService().isProductFlagEnabled(productName, productFlags, flipperFlags);
  });

export const isProductStrictlyEnabled: (state: RootState, productName: LegacyProductFlag | DarklyProductFlag) => boolean =
  createSelector(getProductFlagValues, getProductName, (productFlags, productName) => {
    return new ProductFlagService().isProductFlagEnabled(productName, productFlags, {});
  });

export const hasLegacyProductSuite: (state: RootState, productSuite: LegacyProductSuite) => boolean = createSelector(
  getFlipperFlagValues,
  getProductFlagValues,
  getProductSuite,
  (flipperFlags, productFlags, productSuite) => {
    return LEGACY_PRODUCT_SUITE_FLAGS[productSuite].every((product) => {
      return new ProductFlagService().isProductFlagEnabled(product, productFlags, flipperFlags);
    });
  }
);

export const isVidaliaEnabled: (state: RootState) => boolean = createSelector(getProductFlagValues, (productFlags) => {
  return new VidaliaProductService(productFlags).isVidaliaEnabled();
});

export const isVidaliaProductEnabled: (state: RootState, darklyProductFlag: string) => boolean = createSelector(
  getProductFlagValues,
  getProductName,
  (productFlags, darklyProductFlag) => {
    return new VidaliaProductService(productFlags).isProductFlagEnabled(darklyProductFlag);
  }
);

export const isVidaliaOrLegacyProductEnabled: (
  state: RootState,
  legacyProductFlag: LegacyProductFlag,
  darklyProductFlag: DarklyProductFlag
) => boolean = createSelector(
  isVidaliaEnabled,
  (state: RootState, legacyProductFlag: LegacyProductFlag) => isProductEnabled(state, legacyProductFlag),
  (state: RootState, _: LegacyProductFlag, darklyProductFlag: DarklyProductFlag) =>
    isVidaliaProductEnabled(state, darklyProductFlag),
  (vidaliaEnabled, legacyProductEnabled: boolean, darklyProductEnabled: boolean) => {
    return vidaliaEnabled ? darklyProductEnabled : legacyProductEnabled;
  }
);

export const isVidaliaProductsOrLegacyProductEnabled: (state: RootState, legacyProductFlag: LegacyProductFlag) => boolean =
  createSelector(
    isVidaliaEnabled,
    (state: RootState, legacyProductFlag: LegacyProductFlag) => isProductEnabled(state, legacyProductFlag),
    (vidaliaEnabled: boolean, legacyProductEnabled: boolean) => {
      return vidaliaEnabled || legacyProductEnabled;
    }
  );

export const isVidaliaRouteEnabled: (state: RootState, darklyProductFlag: string) => boolean = createSelector(
  getProductFlagValues,
  getProductName,
  (productFlags, darklyProductFlag) => {
    return new VidaliaProductService(productFlags).isProductRouteEnabled(darklyProductFlag);
  }
);

/**
 * Selector that determines whether the
 * Touch Point Analytics and Dashboard Reporting feature is enabled.
 *
 * This selector checks three conditions:
 * - Whether the `VIDALIA` flag is enabled.
 * - Whether the `TOUCH_POINT_ANALYTICS_AND_DASHBOARD_REPORTING` flag is enabled.
 * - Whether the `FULL_ANALYTICS_AND_DASHBOARD_REPORTING` flag is NOT enabled.
 *
 * @returns {boolean} `true` if the feature should be enabled based on the combination of flags and Vidalia status, `false` otherwise.
 */
export const isTouchPointAnalyticsAndDashboardReportingEnabled: (state: RootState) => boolean = createSelector(
  isVidaliaEnabled,
  (state: RootState) => isVidaliaProductEnabled(state, DARKLY_PRODUCT_FLAGS.TOUCH_POINT_ANALYTICS_AND_DASHBOARD_REPORTING),
  (state: RootState) => isVidaliaProductEnabled(state, DARKLY_PRODUCT_FLAGS.FULL_ANALYTICS_AND_DASHBOARD_REPORTING),
  (
    vidaliaEnabled: boolean,
    touchPointAnalyticsAndDashboardReportingEnabled: boolean,
    fullAnalyticsAndDashboardReportingEnabled: boolean
  ) => {
    return vidaliaEnabled && touchPointAnalyticsAndDashboardReportingEnabled && !fullAnalyticsAndDashboardReportingEnabled;
  }
);
